/**
 * WorkforceStrip — the Dashboard's "Your AI workforce" section.
 *
 * One card per deployed agent, answering the operator's real question
 * ("what do these agents even DO?") in plain English: the template's job
 * one-liner (`agentPurpose`), the honest last-run time from the agent's
 * real record ('—' when it has never run), a live count of ITS proposals
 * waiting in the approvals inbox (`pendingProposalsFor`), and a "Run now"
 * button wired to the EXISTING run mechanism (`runAgentOnce` — the same
 * governed path the Agents view uses, so a run only ever QUEUES proposals,
 * never auto-writes). The header deep-links to the full Agents view.
 *
 * Self-contained like AgentFleetWidget: subscribes to the agents +
 * approvals collections itself, so hosts just render it with the
 * `onOpenAgents` deep-link callback. Honest empty state when no agents
 * are deployed.
 */

import { useEffect, useState } from 'react';
import { ArrowUpRight, Bot, Inbox, Play, Radio } from 'lucide-react';
import { GlassPanel } from './GlassPanel';
import { C } from '../lib/tokens';
import { agentTemplate, runAgentOnce, subscribeAgents, updateAgent } from '../lib/db/agents';
import { subscribeApprovals } from '../lib/agent/approvals';
import { agentPurpose, lastRunLabel, pendingProposalsFor } from '../lib/agent/workforce';
import { isLiveEligible, nextLiveRunIso } from '../lib/agent/liveSchedule';
import type { ApprovalItem, DeployedAgent } from '../lib/db/types';

/**
 * Human label for the agent's persisted `nextRunAt`. In the past reads as
 * "due now" — the 60s ticker will pick it up on its next check.
 */
function nextRunLabel(iso: string | null | undefined, now = Date.now()): string {
  if (!iso) return 'due now';
  const diff = new Date(iso).getTime() - now;
  if (Number.isNaN(diff) || diff <= 0) return 'due now';
  const mins = Math.round(diff / 60_000);
  if (mins < 1) return 'in <1m';
  if (mins < 60) return `in ${mins}m`;
  const hrs = Math.round(mins / 60);
  if (hrs < 24) return `in ${hrs}h`;
  return `in ${Math.round(hrs / 24)}d`;
}

export interface WorkforceStripProps {
  /** False for viewers — "Run now" affordances hidden, not errored. */
  canMutate: boolean;
  /** Deep-link to the full Agents view (Projects → Agents). */
  onOpenAgents: () => void;
}

export function WorkforceStrip({ canMutate, onOpenAgents }: WorkforceStripProps) {
  const [agents, setAgents] = useState<DeployedAgent[]>([]);
  const [approvals, setApprovals] = useState<ApprovalItem[]>([]);
  const [busyId, setBusyId] = useState<string | null>(null);

  useEffect(() => {
    const unsubAgents = subscribeAgents(setAgents);
    const unsubApprovals = subscribeApprovals(setApprovals);
    return () => {
      unsubAgents();
      unsubApprovals();
    };
  }, []);

  const runNow = async (id: string) => {
    if (busyId) return;
    setBusyId(id);
    try {
      // The EXISTING governed run path: proposals land in the approvals
      // inbox; nothing is written until a human approves.
      await runAgentOnce(id, { trigger: 'manual' });
    } finally {
      setBusyId(null);
    }
  };

  // Flip the per-agent live schedule. Ticking is safe-by-design (runs only
  // queue proposals), so this is a per-agent opt-in independent of the
  // global autonomy switch. Persisted on the record; cards update live.
  const toggleLive = async (agent: DeployedAgent) => {
    const next = !(agent.liveSchedule === true);
    await updateAgent(agent.id, {
      liveSchedule: next,
      // Stamp a projected next run when arming so the card answers "when?"
      // immediately; a never-ran agent is due on the ticker's next check.
      nextRunAt: next && agent.lastRunAt ? nextLiveRunIso(agent.cadence, agent.lastRunAt) : agent.nextRunAt ?? null,
    });
  };

  return (
    <GlassPanel
      variant="subtle"
      animate={false}
      style={{ padding: '16px 20px' }}
      aria-label="Your AI workforce"
    >
      <div
        style={{
          display: 'flex',
          alignItems: 'center',
          justifyContent: 'space-between',
          gap: 12,
          flexWrap: 'wrap',
        }}
      >
        <div style={{ display: 'flex', alignItems: 'center', gap: 10, minWidth: 0 }}>
          <span
            aria-hidden="true"
            style={{
              width: 32,
              height: 32,
              borderRadius: 10,
              display: 'inline-grid',
              placeItems: 'center',
              flex: '0 0 auto',
              background: 'linear-gradient(135deg, rgba(255,0,255,0.22), rgba(187,65,152,0.12))',
              border: '1px solid rgba(255,255,255,0.14)',
              color: '#fff',
            }}
          >
            <Bot size={16} strokeWidth={1.9} />
          </span>
          <div style={{ minWidth: 0 }}>
            <span className="t-eyebrow">Agents</span>
            <h2 className="t-section-title" style={{ margin: '2px 0 0' }}>
              Your AI workforce
            </h2>
          </div>
        </div>
        <div style={{ display: 'flex', alignItems: 'center', gap: 8, flexWrap: 'wrap' }}>
          <span className="t-meta" style={{ color: C.textDim }}>
            governed — runs queue proposals, never auto-write
          </span>
          <button className="glass-btn" onClick={onOpenAgents} title="Open Projects → Agents">
            All agents <ArrowUpRight size={12} aria-hidden="true" />
          </button>
        </div>
      </div>

      {agents.length === 0 ? (
        // Honest empty state — no fabricated fleet.
        <div className="t-meta" style={{ marginTop: 12, color: C.textMuted }}>
          No agents deployed yet — launch one from a template under Projects → Agents.
        </div>
      ) : (
        <ul
          style={{
            listStyle: 'none',
            margin: '14px 0 0',
            padding: 0,
            display: 'grid',
            gap: 10,
            gridTemplateColumns: 'repeat(auto-fit, minmax(250px, 1fr))',
          }}
        >
          {agents.map((agent) => {
            const pending = pendingProposalsFor(agent.template, approvals);
            const lastRun = lastRunLabel(agent.lastRunAt);
            return (
              <li
                key={agent.id}
                aria-label={`${agent.name} — deployed agent`}
                style={{
                  display: 'grid',
                  gap: 8,
                  alignContent: 'start',
                  padding: '12px 14px',
                  borderRadius: 12,
                  background: 'rgba(255,255,255,0.03)',
                  border: '1px solid rgba(255,255,255,0.08)',
                }}
              >
                <div style={{ display: 'flex', alignItems: 'baseline', gap: 8, minWidth: 0 }}>
                  <span className="t-card-title" style={{ fontSize: 13, minWidth: 0 }}>
                    {agent.name}
                  </span>
                  <span className="t-meta" style={{ color: C.textDim, flex: '0 0 auto' }}>
                    {agentTemplate(agent.template).label}
                  </span>
                </div>
                {/* What it does, in plain English. */}
                <span className="t-meta" style={{ color: C.textMuted }}>
                  {agentPurpose(agent.template)}
                </span>
                <div
                  style={{
                    display: 'flex',
                    alignItems: 'center',
                    gap: 8,
                    flexWrap: 'wrap',
                    marginTop: 2,
                  }}
                >
                  <span
                    className="t-meta"
                    style={{ color: C.textMuted }}
                    title={agent.lastRunAt ?? 'This agent has never run'}
                  >
                    Last run {lastRun}
                  </span>
                  <span
                    className={pending > 0 ? 'glass-pill glass-pill--orange' : 'glass-pill'}
                    title="Proposals from this agent's domain waiting in the Dashboard approvals inbox"
                    style={{ display: 'inline-flex', alignItems: 'center', gap: 4 }}
                  >
                    <Inbox size={10} aria-hidden="true" />
                    {pending} pending
                  </span>
                  {isLiveEligible(agent) && (
                    <span
                      className="glass-pill glass-pill--green"
                      title="Runs on schedule while the app is open — proposals still need your approval"
                      style={{ display: 'inline-flex', alignItems: 'center', gap: 4 }}
                    >
                      <Radio size={10} aria-hidden="true" />
                      live · next {nextRunLabel(agent.nextRunAt)}
                    </span>
                  )}
                  <span style={{ flex: 1 }} />
                  {canMutate && agent.cadence !== 'manual' && (
                    <button
                      type="button"
                      className="glass-btn"
                      aria-pressed={agent.liveSchedule === true}
                      aria-label={`Live schedule for ${agent.name}`}
                      onClick={() => void toggleLive(agent)}
                      title="Runs on schedule while the app is open — proposals still need your approval"
                      style={{ display: 'inline-flex', alignItems: 'center', gap: 5 }}
                    >
                      <Radio size={11} aria-hidden="true" />
                      {agent.liveSchedule ? 'Live on' : 'Live off'}
                    </button>
                  )}
                  {canMutate && (
                    <button
                      type="button"
                      className="glass-btn"
                      disabled={busyId !== null}
                      aria-label={`Run ${agent.name} now`}
                      onClick={() => void runNow(agent.id)}
                      title="Run this agent once — any writes it wants are queued as proposals for your approval"
                      style={{ display: 'inline-flex', alignItems: 'center', gap: 5 }}
                    >
                      <Play size={11} aria-hidden="true" />
                      {busyId === agent.id ? 'Running…' : 'Run now'}
                    </button>
                  )}
                </div>
              </li>
            );
          })}
        </ul>
      )}
    </GlassPanel>
  );
}
